import { GameApp } from "./core/game-app";
import { loadMission } from "./mission/loader";
import escortData from "../missions/escort-01.json";
import exfilData from "../missions/exfil-01.json";
import riverValleyData from "../missions/river-valley-01.json";
import ironMarketData from "../missions/iron-market-01.json";
import greenHammerData from "../missions/green-hammer-01.json";
import ironCurtainData from "../missions/iron-curtain-01.json";
import arcticWolfData from "../missions/arctic-wolf-01.json";
import { ROOFTOP_AA_PERCHES } from "./world/map03-city";
import { buildRescueMission } from "./mission/variants";

const canvas = document.getElementById("stage") as HTMLCanvasElement | null;
if (!canvas) throw new Error("Missing #stage canvas");

// The Operation (run) flow is the game; these cards are the TEST RANGE list
// behind the menu — authored one-off missions for practice, nothing banked.
// Rooftop guns are engine-side config attached post-load (flak never lives
// in mission JSON).
const ironMarketConfig = loadMission(ironMarketData as Parameters<typeof loadMission>[0]);
ironMarketConfig.flakSites = ROOFTOP_AA_PERCHES.map(([x, z]) => ({ kind: "aa" as const, origin: [x, 22, z] as [number, number, number] }));
ironMarketConfig.armorUnits = [
  { kind: "truck", origin: [430, 0, 140] },
  { kind: "truck", origin: [430, 0, 370] },
];

const greenHammerConfig = loadMission(greenHammerData as Parameters<typeof loadMission>[0]);
greenHammerConfig.flakSites = [
  { kind: "aa", origin: [-10, 0, 6] },
  { kind: "aa", origin: [424, 0, 378] },
];
greenHammerConfig.armorUnits = [
  { kind: "truck", origin: [310, 0, 40] },
  { kind: "truck", origin: [430, 0, 352] },
];

const ironCurtainConfig = loadMission(ironCurtainData as Parameters<typeof loadMission>[0]);
ironCurtainConfig.flakSites = [
  { kind: "aa", origin: [-20, 0, -110] },
  { kind: "aa", origin: [300, 0, 452] },
];
ironCurtainConfig.armorUnits = [
  { kind: "tank", origin: [130, 0, 40] },
  { kind: "truck", origin: [438, 0, 348] },
];

const arcticWolfConfig = loadMission(arcticWolfData as Parameters<typeof loadMission>[0]);
arcticWolfConfig.flakSites = [
  { kind: "aa", origin: [-12, 0, -2] },
  { kind: "manpads", origin: [150, 0, 90] },
  { kind: "aa", origin: [420, 0, 372] },
];
arcticWolfConfig.armorUnits = [
  { kind: "tank", origin: [128, 0, 34] },
  { kind: "truck", origin: [436, 0, 350] },
];

// RESCUE runs the real node builder — the only dual-squad mission, on the
// range so the link-up loop can be played (and boot-verified) directly.
const rescueConfig = buildRescueMission(
  { id: "range-rescue", kind: "rescue", col: 1, row: 0, variant: 0, next: [] },
  { countMult: 1, budgetDelta: 0, squadSize: 4, minSurvivors: 1 },
);

const testRangeCards = [
  {
    config: loadMission(riverValleyData as Parameters<typeof loadMission>[0]),
    desc: "The full river-valley escort as a standalone sortie: checkpoint, hamlet, bridge fork, low town, LZ hold.",
    hint: "The river is impassable — the bridge is the only crossing. The nest covers it; vehicles crash the exfil.",
  },
  {
    config: ironMarketConfig,
    desc: "OPERATION IRON MARKET — escort Delta through the market street, the plaza, and the civic block to a vehicle extraction.",
    hint: "Rooftop guns cover the market and the lot. The streets are narrow — Delta is danger close to everything.",
  },
  {
    config: greenHammerConfig,
    desc: "OPERATION GREEN HAMMER — move a CIA officer from the river landing, across the ford, through the grove to a border LZ.",
    hint: "The jungle fights from ambush. Your thermal cuts the canopy — Alpha's eyes can't. Watch the far bank at the ford.",
  },
  {
    config: ironCurtainConfig,
    desc: "OPERATION IRON CURTAIN — extract a NATO asset through border farmland: treelines, trenches, the guard post, the wire.",
    hint: "Armor at the guard post — 25mm ricochets, bring the 105. Mobile AA covers the strip. Keep it surgical.",
  },
  {
    config: arcticWolfConfig,
    desc: "OPERATION ARCTIC WOLF — bring a cut-off FSK team across the frozen lake and over the saddle to a fjord extraction.",
    hint: "On the lake ice the team is exposed for a kilometer. Densest AA of any theater — flares warm, altitude honest.",
  },
  {
    config: rescueConfig,
    desc: "RESCUE: RAVEN 3-3 — a downed helicopter crew is under siege in the valley. Reach them, then bring everyone home.",
    hint: "RAVEN is inside your blast radius the whole approach. Two elements, one debrief — keep both alive to the link-up.",
  },
  {
    config: loadMission(escortData as Parameters<typeof loadMission>[0]),
    desc: "Legacy escort map. Three checkpoints, ambushes at each. Useful for weapons practice.",
    hint: "Prioritize threats near the squad. Use howitzer for clustered advances.",
  },
  {
    config: loadMission(exfilData as Parameters<typeof loadMission>[0]),
    desc: "Hold the LZ until all hostiles are neutralized.",
    hint: "Reinforce wave inbound at low enemy count — don't let it hit full strength.",
  },
];

const app = new GameApp(canvas, testRangeCards);
app.start();

const loaderDone = (window as unknown as { __loaderDone?: () => void }).__loaderDone;
if (loaderDone) loaderDone();
